import React from "react";
import LegalLayout from "@/components/LegalLayout";
import { useI18n } from "@/context/I18nContext";

export default function Terms() {
  const { t, lang } = useI18n();
  return (
    <LegalLayout
      kicker={t("legal.terms_kicker")}
      title={t("legal.terms_title")}
      lastUpdated={lang === "da" ? "Maj 2026" : "May 2026"}
    >
      {lang === "da" ? <DanishTerms /> : <EnglishTerms />}
    </LegalLayout>
  );
}

function EnglishTerms() {
  return (
    <>
      <p>
        Welcome to TeamFit. By accessing or using the TeamFit app, website, and services, you agree to these Terms of Service.
      </p>
      <p>If you do not agree with these terms, please do not use TeamFit.</p>

      <h2>1. About TeamFit</h2>
      <p>TeamFit is a social sports platform where users can:</p>
      <ul>
        <li>Create and join sports sessions</li>
        <li>Communicate with other users</li>
        <li>Share locations and routes</li>
        <li>Upload content</li>
        <li>Participate in community activities</li>
      </ul>

      <h2>2. Eligibility</h2>
      <p>Users must comply with local laws to use TeamFit.</p>
      <p>Users under 18 years old should use the app with parental guidance.</p>

      <h2>3. User Accounts</h2>
      <p>Users are responsible for:</p>
      <ul>
        <li>Maintaining account security</li>
        <li>Keeping login credentials confidential</li>
        <li>Ensuring profile information is accurate</li>
      </ul>
      <p>Users may register using:</p>
      <ul>
        <li>Apple Login</li>
        <li>Google Login</li>
      </ul>

      <h2>4. User Conduct</h2>
      <p>Users agree NOT to:</p>
      <ul>
        <li>Harass or abuse others</li>
        <li>Share illegal or harmful content</li>
        <li>Use fake identities</li>
        <li>Spam or scam other users</li>
        <li>Upload offensive or inappropriate material</li>
        <li>Misuse live location or chat features</li>
      </ul>
      <p>TeamFit reserves the right to suspend or terminate accounts violating these rules.</p>

      <h2>5. Sports Activities Disclaimer</h2>
      <p><strong>Users participate in sports activities at their own risk.</strong></p>
      <p>TeamFit is not responsible for:</p>
      <ul>
        <li>Injuries</li>
        <li>Accidents</li>
        <li>Health issues</li>
        <li>User behavior during activities</li>
        <li>Disputes between users</li>
      </ul>
      <p>Users are responsible for their own safety and physical condition.</p>

      <h2>6. Location Services</h2>
      <p>Some features require GPS and live location sharing.</p>
      <p>Users can disable location permissions at any time through device settings.</p>

      <h2>7. Content Ownership</h2>
      <p>Users retain ownership of content they upload, including:</p>
      <ul>
        <li>Photos</li>
        <li>Session images</li>
        <li>Messages</li>
        <li>Reviews</li>
      </ul>
      <p>By uploading content, users grant TeamFit permission to display and use the content within the platform.</p>

      <h2>8. Subscriptions and Payments</h2>
      <p>TeamFit offers optional premium subscriptions ("TeamFit Pro"). The current Pro plans and prices are:</p>
      <ul>
        <li>Monthly plan: <strong>DKK 29 / month</strong>, billed every month.</li>
        <li>Annual plan: <strong>DKK 290 / year</strong>, billed once per year (approximately 17% savings compared to monthly).</li>
      </ul>
      <p>All prices include Danish VAT (MOMS) where applicable. Prices may be updated from time to time; existing subscribers will be given at least 30 days' notice before any price increase takes effect and may cancel before the change applies.</p>

      <h3>8.1 Free trial</h3>
      <p>New users starting their first paid plan receive a <strong>14-day free trial</strong>. No charge is made during the trial. If you do not cancel before the trial ends, the plan will automatically convert to a paid subscription and your payment method will be charged the price above. Only one free trial is available per user.</p>

      <h3>8.2 Automatic renewal</h3>
      <p>Subscriptions <strong>renew automatically</strong> at the end of each billing period (monthly or annually) until cancelled. Your payment method will be charged on each renewal date at the then-current price.</p>

      <h3>8.3 Payment method</h3>
      <p>Payments on the TeamFit web platform (teamfit.dk) are processed securely by our payment provider <strong>Stripe Payments Europe, Ltd.</strong>. Accepted payment methods include major credit and debit cards (Visa, Mastercard, American Express) and other methods enabled by Stripe. TeamFit does not store your full card number.</p>
      <p>If you subscribe through the TeamFit mobile app instead, payments may alternatively be processed through Apple App Store (in-app purchases) or Google Play, subject to the terms of those platforms.</p>

      <h3>8.4 Cancellation</h3>
      <p>You can cancel your subscription at any time from your TeamFit profile ("Manage subscription" → Stripe Customer Portal → Cancel plan). Cancellation stops future renewals but does <strong>not</strong> generate an automatic refund for the current period — your Pro access remains active until the end of the paid period.</p>

      <h3>8.5 Right of withdrawal (EU / DK consumers)</h3>
      <p>As a consumer in the EU / Denmark, you have a statutory 14-day right of withdrawal for digital services. However, by starting your free trial and/or paid subscription, you <strong>expressly consent</strong> to the immediate performance of the service and <strong>acknowledge that your right of withdrawal is lost once the service is fully performed</strong>, i.e., once you have used TeamFit Pro features during your billing period. Trial usage does not count as performance for withdrawal purposes; you may cancel during the trial at no cost.</p>

      <h3>8.6 Failed payments</h3>
      <p>If a scheduled payment fails, TeamFit will attempt to retry the charge over the following days (per Stripe's standard dunning process). You will be notified by email and asked to update your payment method. If payment is not received, your Pro access will be suspended until the balance is settled.</p>

      <h3>8.7 Refunds</h3>
      <p>Except where required by applicable law, subscription fees are non-refundable. If you believe you have been charged in error, contact <a href="mailto:support@teamfit.dk">support@teamfit.dk</a> within 30 days and we will review your case in good faith.</p>

      <h3>8.8 Referral rewards</h3>
      <p>Users who refer a friend who successfully subscribes to a paid plan (not a trial) receive 30 additional days of free Pro access, credited to the referrer's account. Referral rewards have no cash value and cannot be exchanged, transferred, or converted to a refund. TeamFit reserves the right to void rewards obtained through fraudulent or abusive activity.</p>


      <h2>9. Account Deletion</h2>
      <p>Users may delete their account at any time.</p>
      <p>After deletion:</p>
      <ul>
        <li>Personal data will be removed or anonymized within a reasonable timeframe</li>
        <li>Some information may be retained if legally required</li>
      </ul>

      <h2>10. Intellectual Property</h2>
      <p>All TeamFit branding, logos, designs, and platform content belong to TeamFit unless otherwise stated.</p>
      <p>Users may not copy or redistribute TeamFit materials without permission.</p>

      <h2>11. Service Availability</h2>
      <p>We may update, modify, or temporarily suspend parts of the service without prior notice.</p>
      <p>We do not guarantee uninterrupted availability.</p>

      <h2>12. Limitation of Liability</h2>
      <p>TeamFit is provided "as is" without warranties of any kind.</p>
      <p>To the maximum extent permitted by law, TeamFit shall not be liable for:</p>
      <ul>
        <li>Indirect damages</li>
        <li>Data loss</li>
        <li>Personal injury</li>
        <li>User disputes</li>
        <li>Financial losses</li>
      </ul>

      <h2>13. Changes to Terms</h2>
      <p>We may update these Terms from time to time.</p>
      <p>Continued use of TeamFit after changes means you accept the updated Terms.</p>

      <h2>14. Contact</h2>
      <p>
        TeamFit<br />
        Website: <a href="https://teamfit.dk">teamfit.dk</a><br />
        Email: <a href="mailto:support@teamfit.dk">support@teamfit.dk</a>
      </p>
    </>
  );
}

function DanishTerms() {
  return (
    <>
      <p>
        Velkommen til TeamFit. Ved at tilgå eller bruge TeamFit-appen, hjemmesiden og tjenesterne accepterer du disse brugervilkår.
      </p>
      <p>Hvis du ikke accepterer disse vilkår, bedes du ikke bruge TeamFit.</p>

      <h2>1. Om TeamFit</h2>
      <p>TeamFit er en social sportsplatform, hvor brugere kan:</p>
      <ul>
        <li>Oprette og deltage i sportssessioner</li>
        <li>Kommunikere med andre brugere</li>
        <li>Dele lokationer og ruter</li>
        <li>Uploade indhold</li>
        <li>Deltage i fællesskabsaktiviteter</li>
      </ul>

      <h2>2. Adgangskrav</h2>
      <p>Brugere skal overholde lokale love for at bruge TeamFit.</p>
      <p>Brugere under 18 år bør bruge appen under forældrenes vejledning.</p>

      <h2>3. Brugerkonti</h2>
      <p>Brugere er ansvarlige for at:</p>
      <ul>
        <li>Opretholde kontosikkerhed</li>
        <li>Holde loginoplysninger fortrolige</li>
        <li>Sikre at profiloplysningerne er korrekte</li>
      </ul>
      <p>Du kan registrere dig med:</p>
      <ul>
        <li>Apple-login</li>
        <li>Google-login</li>
      </ul>

      <h2>4. Brugeradfærd</h2>
      <p>Brugere accepterer IKKE at:</p>
      <ul>
        <li>Chikanere eller mishandle andre</li>
        <li>Dele ulovligt eller skadeligt indhold</li>
        <li>Bruge falske identiteter</li>
        <li>Spamme eller bedrage andre brugere</li>
        <li>Uploade stødende eller upassende materiale</li>
        <li>Misbruge live-position eller chatfunktioner</li>
      </ul>
      <p>TeamFit forbeholder sig retten til at suspendere eller lukke konti, der overtræder disse regler.</p>

      <h2>5. Ansvarsfraskrivelse for sportsaktiviteter</h2>
      <p><strong>Brugere deltager i sportsaktiviteter på eget ansvar.</strong></p>
      <p>TeamFit er ikke ansvarlig for:</p>
      <ul>
        <li>Skader</li>
        <li>Ulykker</li>
        <li>Helbredsproblemer</li>
        <li>Brugeradfærd under aktiviteter</li>
        <li>Uenigheder mellem brugere</li>
      </ul>
      <p>Brugere er selv ansvarlige for deres egen sikkerhed og fysiske tilstand.</p>

      <h2>6. Lokationstjenester</h2>
      <p>Nogle funktioner kræver GPS og live-positionsdeling.</p>
      <p>Du kan til enhver tid deaktivere lokationstilladelser via din enheds indstillinger.</p>

      <h2>7. Ejerskab af indhold</h2>
      <p>Brugere beholder ejerskabet af det indhold, de uploader, herunder:</p>
      <ul>
        <li>Billeder</li>
        <li>Sessionsbilleder</li>
        <li>Beskeder</li>
        <li>Anmeldelser</li>
      </ul>
      <p>Ved at uploade indhold giver du TeamFit tilladelse til at vise og bruge indholdet inden for platformen.</p>

      <h2>8. Abonnementer og betalinger</h2>
      <p>TeamFit tilbyder valgfrie premium-abonnementer ("TeamFit Pro"). De aktuelle Pro-planer og priser er:</p>
      <ul>
        <li>Månedlig plan: <strong>29 kr. / md.</strong>, opkræves hver måned.</li>
        <li>Årlig plan: <strong>290 kr. / år</strong>, opkræves én gang om året (ca. 17% besparelse i forhold til månedlig betaling).</li>
      </ul>
      <p>Alle priser inkluderer dansk moms hvor det er relevant. Priserne kan opdateres fra tid til anden; eksisterende abonnenter vil få mindst 30 dages varsel før en prisstigning træder i kraft og kan opsige inden ændringen gælder.</p>

      <h3>8.1 Gratis prøveperiode</h3>
      <p>Nye brugere, der starter deres første betalte plan, får en <strong>14-dages gratis prøveperiode</strong>. Der opkræves intet i prøveperioden. Hvis du ikke opsiger inden prøveperioden slutter, konverteres planen automatisk til et betalt abonnement, og din betalingsmetode opkræves prisen ovenfor. Der er kun én gratis prøveperiode pr. bruger.</p>

      <h3>8.2 Automatisk fornyelse</h3>
      <p>Abonnementer <strong>fornyes automatisk</strong> ved afslutningen af hver betalingsperiode (månedligt eller årligt), indtil de opsiges. Din betalingsmetode opkræves på hver fornyelsesdato til den til enhver tid gældende pris.</p>

      <h3>8.3 Betalingsmetode</h3>
      <p>Betalinger på TeamFits webplatform (teamfit.dk) behandles sikkert af vores betalingsudbyder <strong>Stripe Payments Europe, Ltd.</strong>. Accepterede betalingsmetoder inkluderer store kredit- og debitkort (Visa, Mastercard, American Express) og andre metoder aktiveret af Stripe. TeamFit gemmer ikke dit fulde kortnummer.</p>
      <p>Hvis du abonnerer via TeamFits mobilapp, kan betalinger alternativt behandles gennem Apple App Store (in-app-køb) eller Google Play i henhold til disse platformes vilkår.</p>

      <h3>8.4 Opsigelse</h3>
      <p>Du kan til enhver tid opsige dit abonnement fra din TeamFit-profil ("Administrér abonnement" → Stripe Customer Portal → Annullér plan). Opsigelse stopper fremtidige fornyelser, men udløser <strong>ikke</strong> automatisk refusion for den nuværende periode — din Pro-adgang forbliver aktiv indtil slutningen af den betalte periode.</p>

      <h3>8.5 Fortrydelsesret (EU / DK forbrugere)</h3>
      <p>Som forbruger i EU / Danmark har du en lovbestemt 14-dages fortrydelsesret for digitale tjenester. Ved at starte din gratis prøveperiode og/eller betalte abonnement <strong>giver du udtrykkeligt samtykke</strong> til øjeblikkelig levering af tjenesten og <strong>anerkender, at din fortrydelsesret bortfalder, når tjenesten er fuldt leveret</strong>, dvs. når du har brugt TeamFit Pro-funktioner i din betalingsperiode. Brug i prøveperioden tæller ikke som levering ift. fortrydelsesretten; du kan opsige i prøveperioden uden omkostninger.</p>

      <h3>8.6 Fejlslagne betalinger</h3>
      <p>Hvis en planlagt betaling fejler, vil TeamFit forsøge at genopkræve i de følgende dage (jf. Stripes standard-dunning-proces). Du vil blive underrettet via e-mail og bedt om at opdatere din betalingsmetode. Hvis betaling ikke modtages, suspenderes din Pro-adgang, indtil saldoen er afregnet.</p>

      <h3>8.7 Refusion</h3>
      <p>Medmindre gældende lovgivning kræver det, refunderes abonnementsgebyrer ikke. Hvis du mener, at du er blevet opkrævet ved en fejl, kontakt <a href="mailto:support@teamfit.dk">support@teamfit.dk</a> inden for 30 dage, og vi vil behandle din sag i god tro.</p>

      <h3>8.8 Henvisningsbelønninger</h3>
      <p>Brugere, der henviser en ven, som succesfuldt tegner et betalt abonnement (ikke en prøveperiode), modtager 30 ekstra dages gratis Pro-adgang, krediteret henviserens konto. Henvisningsbelønninger har ingen kontantværdi og kan ikke ombyttes, overføres eller konverteres til refusion. TeamFit forbeholder sig retten til at annullere belønninger opnået gennem svigagtig eller misbrugende aktivitet.</p>


      <h2>9. Sletning af konto</h2>
      <p>Brugere kan til enhver tid slette deres konto.</p>
      <p>Efter sletning:</p>
      <ul>
        <li>Personoplysninger fjernes eller anonymiseres inden for en rimelig tidsramme</li>
        <li>Visse oplysninger kan opbevares, hvis det er lovpligtigt</li>
      </ul>

      <h2>10. Immaterielle rettigheder</h2>
      <p>Al TeamFit-branding, logoer, designs og platformsindhold tilhører TeamFit, medmindre andet er angivet.</p>
      <p>Brugere må ikke kopiere eller videredistribuere TeamFit-materialer uden tilladelse.</p>

      <h2>11. Tjenestens tilgængelighed</h2>
      <p>Vi kan opdatere, ændre eller midlertidigt suspendere dele af tjenesten uden forudgående varsel.</p>
      <p>Vi garanterer ikke uafbrudt tilgængelighed.</p>

      <h2>12. Ansvarsbegrænsning</h2>
      <p>TeamFit leveres "som det er" uden nogen form for garantier.</p>
      <p>I det omfang loven tillader det, er TeamFit ikke ansvarlig for:</p>
      <ul>
        <li>Indirekte skader</li>
        <li>Datatab</li>
        <li>Personskade</li>
        <li>Uenigheder mellem brugere</li>
        <li>Økonomiske tab</li>
      </ul>

      <h2>13. Ændringer af vilkår</h2>
      <p>Vi kan opdatere disse vilkår fra tid til anden.</p>
      <p>Fortsat brug af TeamFit efter ændringer betyder, at du accepterer de opdaterede vilkår.</p>

      <h2>14. Kontakt</h2>
      <p>
        TeamFit<br />
        Hjemmeside: <a href="https://teamfit.dk">teamfit.dk</a><br />
        E-mail: <a href="mailto:support@teamfit.dk">support@teamfit.dk</a>
      </p>
    </>
  );
}
